import React, { useCallback, useMemo } from 'react'
import { Col } from 'antd'
import {
  filter,
  includes,
  intersection,
  isEmpty,
  isNil,
  map,
  size,
  sortBy,
  toString,
} from 'lodash'

import { GridFormItem } from '../../../core/components/sections'
import { getFilterOptions } from '../../../core/utils'

import { Select } from '../../../common/components/sections'
import {
  StyledFullWidthSearchInput,
  StyledPaddingBottomRow,
} from '../../../common/components/styled'
import {
  ALL_SELECT_FILTER_ITEM,
  ALL_SELECT_OPTION_ITEM,
  EXCLUSION_STATUS,
  PAGINATION,
} from '../../../common/constants'
import { useUsersQuery } from '../../../common/hooks'

import { useLoggedInUser } from '../../../account/hooks'
import {
  DEFAULT_MENU_CURATION_TASK_LIST_FILTERS,
  MENU_CURATION_TASK_NO_ERROR_TAG_FILTER,
  MENU_CURATION_TASK_NO_TAG_FILTER,
  MENU_CURATION_TASK_STATUS,
  MENU_CURATION_TASK_TAGS,
} from '../../constants'
import { useMenuCurationTasksListFilters } from '../../hooks'

const MenuCurationTaskScrapingUserFilters = () => {
  const {
    taskStatus,
    autoeatStatus,
    searchText,
    assignedScrapingReviewerUser,
    tags,
    setFilters,
  } = useMenuCurationTasksListFilters()
  const user = useLoggedInUser()

  const { data: usersData } = useUsersQuery()

  const statusOptions = useMemo(
    () => [
      MENU_CURATION_TASK_STATUS.AWAITING_SCRAPING_REVIEW,
      MENU_CURATION_TASK_STATUS.SCRAPING_REVIEW_IN_PROGRESS,
      MENU_CURATION_TASK_STATUS.AWAITING_AUTOEAT_UPDATES,
      MENU_CURATION_TASK_STATUS.NEEDS_SCRAPING_CLARIFICATION,
    ],
    [],
  )

  const autoeatStatusOptions = useMemo(
    () => getFilterOptions(EXCLUSION_STATUS, 'label', 'value'),
    [],
  )

  const tagsOptions = useMemo(
    () => [
      MENU_CURATION_TASK_NO_ERROR_TAG_FILTER,
      MENU_CURATION_TASK_NO_TAG_FILTER,
      ...map(
        getFilterOptions(MENU_CURATION_TASK_TAGS, 'label', 'value'),
        option => ({ ...option, disabled: false }),
      ),
    ],
    [],
  )

  const usersOptions = useMemo(
    () => [
      ALL_SELECT_FILTER_ITEM,
      ...sortBy(
        map(usersData, ({ id, name }) => ({ label: name, value: id })),
        ({ value }) => (value === user?.id ? -1 : 1),
      ),
    ],
    [user?.id, usersData],
  )

  const selectedAssignedScrapingReviewerUser = useMemo(
    () =>
      !isNil(assignedScrapingReviewerUser)
        ? toString(assignedScrapingReviewerUser)
        : null,
    [assignedScrapingReviewerUser],
  )

  const handleSetTagsValue = useCallback(
    value => {
      const isNoTagFilterBeingAdded = includes(
        value,
        MENU_CURATION_TASK_NO_TAG_FILTER.value,
      )
      const isNoTagFilterPresent =
        includes(tags, MENU_CURATION_TASK_NO_TAG_FILTER.value) &&
        size(tags) === 1
      if (isNoTagFilterBeingAdded && !isNoTagFilterPresent) {
        return setFilters({
          currentPage: PAGINATION.DEFAULT_PAGE,
          tags: [MENU_CURATION_TASK_NO_TAG_FILTER.value],
        })
      }

      const areOtherTagsBeingAdded = size(value) > 1
      if (
        isNoTagFilterPresent &&
        isNoTagFilterBeingAdded &&
        areOtherTagsBeingAdded
      ) {
        return setFilters({
          currentPage: PAGINATION.DEFAULT_PAGE,
          tags: filter(
            value,
            tag => tag !== MENU_CURATION_TASK_NO_TAG_FILTER.value,
          ),
        })
      }

      const isNoErrorTagFilterBeingAdded = includes(
        value,
        MENU_CURATION_TASK_NO_ERROR_TAG_FILTER.value,
      )
      const isNoErrorTagFilterPresent = includes(
        tags,
        MENU_CURATION_TASK_NO_ERROR_TAG_FILTER.value,
      )
      if (isNoErrorTagFilterBeingAdded && !isNoErrorTagFilterPresent) {
        return setFilters({
          currentPage: PAGINATION.DEFAULT_PAGE,
          tags: filter(
            value,
            tag =>
              !includes(
                [
                  MENU_CURATION_TASK_TAGS.HAS_TIMEOUT_ERROR.value,
                  MENU_CURATION_TASK_TAGS.HAS_UNCAUGHT_AI_ERROR.value,
                  MENU_CURATION_TASK_TAGS.HAS_UNCAUGHT_MENU_CURATOR_ERROR.value,
                ],
                tag,
              ),
          ),
        })
      }

      const areErrorTagsBeingAdded =
        !isEmpty(
          intersection(
            [
              MENU_CURATION_TASK_TAGS.HAS_TIMEOUT_ERROR.value,
              MENU_CURATION_TASK_TAGS.HAS_UNCAUGHT_AI_ERROR.value,
              MENU_CURATION_TASK_TAGS.HAS_UNCAUGHT_MENU_CURATOR_ERROR.value,
            ],
            value,
          ),
        ) && size(value) > 1

      if (
        isNoErrorTagFilterPresent &&
        isNoErrorTagFilterBeingAdded &&
        areErrorTagsBeingAdded
      ) {
        return setFilters({
          currentPage: PAGINATION.DEFAULT_PAGE,
          tags: filter(
            value,
            tag => tag !== MENU_CURATION_TASK_NO_ERROR_TAG_FILTER.value,
          ),
        })
      }

      return setFilters({
        currentPage: PAGINATION.DEFAULT_PAGE,
        tags: value,
      })
    },
    [setFilters, tags],
  )

  const handleSetSearchValue = useCallback(
    value => {
      setFilters({
        currentPage: PAGINATION.DEFAULT_PAGE,
        searchText: value,
      })
    },
    [setFilters],
  )

  const handleSetAssignedScrapingUserValue = useCallback(
    value => {
      setFilters({
        currentPage: PAGINATION.DEFAULT_PAGE,
        assignedScrapingReviewerUser: value,
      })
    },
    [setFilters],
  )

  const handleSetTaskStatusValue = useCallback(
    value => {
      setFilters({
        currentPage: PAGINATION.DEFAULT_PAGE,
        taskStatus: value,
        menuAvailability: ALL_SELECT_OPTION_ITEM.value,
      })
    },
    [setFilters],
  )

  const handleSetAutoeatStatusValue = useCallback(
    value => {
      setFilters({
        currentPage: PAGINATION.DEFAULT_PAGE,
        autoeatStatus:
          value || DEFAULT_MENU_CURATION_TASK_LIST_FILTERS.AUTOEAT_STATUS,
      })
    },
    [setFilters],
  )

  return (
    <>
      <StyledPaddingBottomRow gutter={16}>
        <Col span={6}>
          <StyledFullWidthSearchInput
            placeholder="Search Brands"
            defaultValue={searchText}
            onSearch={handleSetSearchValue}
          />
        </Col>
      </StyledPaddingBottomRow>
      <StyledPaddingBottomRow gutter={16}>
        <GridFormItem span={4} label="Assigned Scraping Reviewer">
          <Select
            allowClear
            showSearch
            options={usersOptions}
            value={selectedAssignedScrapingReviewerUser}
            onChange={handleSetAssignedScrapingUserValue}
          />
        </GridFormItem>
        <GridFormItem span={4} label="Status">
          <Select
            allowClear
            showSearch
            options={statusOptions}
            value={taskStatus}
            onChange={handleSetTaskStatusValue}
          />
        </GridFormItem>
        <GridFormItem span={2} label="Autoeat Status">
          <Select
            allowClear
            showSearch
            options={autoeatStatusOptions}
            value={autoeatStatus}
            onChange={handleSetAutoeatStatusValue}
          />
        </GridFormItem>
        <GridFormItem span={4} label="Tags">
          <Select
            allowClear
            showSearch
            mode="multiple"
            options={tagsOptions}
            value={tags}
            placeholder="Select tags..."
            onChange={handleSetTagsValue}
          />
        </GridFormItem>
      </StyledPaddingBottomRow>
    </>
  )
}

export default MenuCurationTaskScrapingUserFilters
